import { runPrograms } from '@/lib/runs'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { ModelLabel } from '@/components/ModelLabel'
import { Picker } from '@/components/Picker'
import { PageButtons } from '@/components/runs/TaskRolloutCards'
import { useBoardData } from '@/components/useBoardData'
import { fmtDateTime } from '@/lib/format'
import type { RunRecord } from '@/lib/runs'

function statusLabel(run: RunRecord) {
  const status = run.display_status || run.status
  const labels: Record<string, string> = {
    completed_with_warnings: 'With warnings', cancelled: 'Stopped', completed: 'Completed',
    warning: 'Warning', running: 'Running', launching: 'Launching', queued: 'Queued',
    stopping: 'Stopping', failed: 'Failed', stopped: 'Stopped',
  }
  return labels[status] ?? status.replaceAll('_', ' ')
}

function Status({ run }: { run: RunRecord }) {
  const status = run.display_status || run.status
  const tone = status === 'failed' ? 'failed' : status.includes('warning') ? 'warning' : run.active ? 'active' : 'neutral'
  return <span className={`experiment-status ${tone}`}>{statusLabel(run)}</span>
}

function Author({ run }: { run: RunRecord }) {
  return run.origin === 'agent'
    ? <span className="experiment-author" title="Launched by the task authoring agent"><Icon name="agent" size={16} />Agent</span>
    : <span className="experiment-author" title={run.created_by_email}>{run.created_by_email?.split('@')[0] || 'Unknown'}{run.origin === 'cli' ? ' · CLI' : run.origin === 'ci' ? ' · CI' : ''}</span>
}

function elapsed(createdAt: string, now: number) {
  const minutes = Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 60_000))
  return minutes < 1 ? 'Just started' : minutes < 60 ? `${minutes}m elapsed` : `${Math.floor(minutes / 60)}h ${minutes % 60}m elapsed`
}

/** Keep provisioning visible: zero completed jobs can mean the fleet is still starting. */
function fleetLabel(run: RunRecord) {
  const requested = run.configuration.nodes || 1
  const fleet = run.fleet
  if (!fleet || fleet.launch_state === null) return `${requested} ${requested === 1 ? 'node' : 'nodes'}`
  if (fleet.launch_state === 'prepared') return 'Queueing jobs'
  if (fleet.launch_state === 'jobs_enqueued') return 'Waiting for nodes'
  if (!fleet.reporting) return `${requested} ${requested === 1 ? 'node' : 'nodes'} starting`
  if (fleet.preparing) return `${fleet.preparing}/${requested} nodes staging image`
  return `${fleet.polling}/${requested} nodes · ${fleet.busy_slots}/${fleet.slots} slots busy`
}

export function ActiveExperiment({ run, now }: { run: RunRecord; now: number }) {
  const tasks = run.configuration.tasks.length
  const rollouts = run.configuration.rollouts || 1
  const jobs = run.metrics?.jobs ?? tasks * rollouts
  // A job the authoring agent stopped on purpose is finished: it counts as complete.
  const completed = Math.min(jobs, (run.metrics?.completed_jobs ?? 0) + (run.metrics?.stopped_jobs ?? 0))
  const failed = Math.min(jobs - completed, run.metrics?.failed_jobs ?? 0)
  const percentage = (value: number) => `${jobs ? value / jobs * 100 : 0}%`
  return <Link className="experiment-card" to={`/eng/runs/${run.run_id}`}>
    <div className="experiment-card-top"><span>{runPrograms(run.configuration)} · {run.configuration.framework}</span><Status run={run} /></div>
    <h3>{run.name}</h3>
    <ModelLabel model={run.configuration.model} />
    <div className="experiment-progress">
      <div className="experiment-progress-label"><span><strong>{completed}/{jobs}</strong> jobs complete</span><span>{failed > 0 ? <span className="err-text">{failed} failed</span> : elapsed(run.created_at, now)}</span></div>
      <div className="experiment-progress-track" role="img" aria-label={`${completed} of ${jobs} jobs complete, ${failed} failed`}>
        <i className="complete" style={{ width: percentage(completed) }} /><i className="failed" style={{ width: percentage(failed) }} />
      </div>
      <div className="experiment-progress-detail"><span>{tasks} {tasks === 1 ? 'task' : 'tasks'} · {rollouts} {rollouts === 1 ? 'rollout' : 'rollouts'} each</span>{failed > 0 && <span>{elapsed(run.created_at, now)}</span>}</div>
    </div>
    <div className="experiment-card-bottom"><span>{fleetLabel(run)}</span><Author run={run} /></div>
  </Link>
}

export function ExperimentHistory({ runs }: { runs: RunRecord[] }) {
  return <div className="experiment-history">
    <div className="experiment-history-head" aria-hidden="true"><span>Experiment</span><span>Model</span><span>Status</span><span>Mean score</span><span /></div>
    {runs.map(run => <Link className="experiment-history-row" key={run.run_id} to={`/eng/runs/${run.run_id}`}>
      <div className="experiment-history-name"><strong>{run.name}</strong><div><span title={run.configuration.framework}>{runPrograms(run.configuration)}</span><span>{fmtDateTime(run.created_at)}</span><Author run={run} /></div></div>
      <ModelLabel model={run.configuration.model} />
      <Status run={run} />
      <span className="experiment-score"><span className="visually-hidden">Mean verifier score </span>{run.metrics?.mean_verifier_score == null ? '—' : run.metrics.mean_verifier_score.toFixed(2)}</span>
      <span className="experiment-open"><Icon name="arrow" size={16} /></span>
    </Link>)}
  </div>
}

const PAGE = 10
// The list mixes website, agent and CLI runs, each group in its own order; sort by start.
const startedAt = (run: RunRecord) => Date.parse(run.created_at) || 0
const newestFirst = (a: RunRecord, b: RunRecord) => startedAt(b) - startedAt(a)

// The backend counts a run as active while queued, launching, running, warning or stopping
// (routes/runs.py _display_status); everything else has finished.
const STATUS_FILTERS: { value: string; label: string; matches: (run: RunRecord) => boolean }[] = [
  { value: 'completed', label: 'Completed', matches: run => run.status === 'completed' },
  { value: 'warnings', label: 'With warnings', matches: run => run.status === 'completed_with_warnings' },
  { value: 'failed', label: 'Failed', matches: run => run.status === 'failed' },
  { value: 'stopped', label: 'Stopped', matches: run => run.status === 'cancelled' || run.status === 'stopped' },
  { value: 'active', label: 'Running or queued', matches: run => run.active },
]
const DEFAULT_STATUS = 'completed'

/** Every experiment by name, including CLI runs, which are otherwise reachable only by
 *  their link once their pool goes quiet (SEL-644). */
export function RecentExperiments() {
  const state = useBoardData<{ active: RunRecord[]; done: RunRecord[] }>('/eng/runs')
  const [params, setParams] = useSearchParams()
  const status = params.get('experiment_status') ?? DEFAULT_STATUS
  const filter = STATUS_FILTERS.find(option => option.value === status)
  const [search, setSearch] = useState('')
  const query = search.trim().toLowerCase()
  // The page belongs to one search and status: a new filter reads as page 1 in the same
  // render, so the clamp below can never restore an old offset over the reset.
  const pageKey = `${status}\n${query}`
  const [paging, setPaging] = useState({ key: pageKey, offset: 0 })
  const offset = paging.key === pageKey ? paging.offset : 0
  const setOffset = (value: number) => setPaging({ key: pageKey, offset: value })
  // Forget the old filter's page, so returning to it (e.g. clearing the search) starts at page 1 too.
  useEffect(() => { if (paging.key !== pageKey) setPaging({ key: pageKey, offset: 0 }) }, [paging.key, pageKey])
  useEffect(() => {
    const timer = window.setInterval(() => { if (!document.hidden) void state.reload() }, 15000)
    return () => clearInterval(timer)
  }, [state.reload])
  const setStatus = (value: string) => setParams(old => {
    const next = new URLSearchParams(old)
    if (value === DEFAULT_STATUS) next.delete('experiment_status'); else next.set('experiment_status', value)
    return next
  }, { replace: true })
  const matchesSearch = (run: RunRecord) => !query || [run.name, run.run_id, run.configuration?.program, run.configuration?.model,
    ...(run.configuration?.models ?? []), run.created_by_email, run.origin].some(value => value?.toLowerCase().includes(query))
  const searched = state.data ? [...state.data.active, ...state.data.done].filter(matchesSearch) : []
  const runs = (filter ? searched.filter(filter.matches) : searched).sort(newestFirst)
  const count = (matches: (run: RunRecord) => boolean) => searched.filter(matches).length
  // A poll can shorten the list under an open page; stay on the last page that still exists.
  const start = Math.min(offset, Math.max(0, Math.ceil(runs.length / PAGE) - 1) * PAGE)
  // Keep the clamp, or a later poll that lengthens the list jumps back to the old page.
  useEffect(() => { if (state.data && start !== offset) setPaging({ key: pageKey, offset: start }) }, [state.data, start, offset, pageKey])
  return <section aria-labelledby="experiments-heading" className="recent-experiments">
    <div className="task-section-head"><h2 id="experiments-heading">Experiments <span className="section-count" aria-hidden="true">{state.data ? runs.length : '—'}</span></h2></div>
    <div className="task-board-toolbar">
      <label className="task-search"><Icon name="search" size={16} /><input type="search" aria-label="Search experiments" placeholder="Find an experiment by name, model or who started it…" value={search} onChange={event => setSearch(event.target.value)} /></label>
      <Picker label="Experiment status" field value={filter ? status : 'all'} onChange={setStatus}
        options={[{ value: 'all', label: `All statuses (${searched.length})` },
          ...STATUS_FILTERS.map(option => ({ value: option.value, label: `${option.label} (${count(option.matches)})` }))]} />
    </div>
    {state.error && <p className="note err">Could not load experiments: {state.error}</p>}
    {!state.data && !state.error && <p className="muted">Loading experiments…</p>}
    {state.data && <>
      {runs.length ? <ExperimentHistory runs={runs.slice(start, start + PAGE)} />
        : <p className="experiments-empty">{filter ? `No experiments are ${filter.label.toLowerCase()}` : 'No experiments'}{query ? ' for this search' : ''}.</p>}
      <PageButtons offset={start} next={start + PAGE < runs.length ? start + PAGE : null} onChange={setOffset} pageSize={PAGE} />
    </>}
  </section>
}
